/**
 * Initialize the configuration for a simple WebGIS
 * based on a simplified template config
 * 
 * @memberof vef.viewer.templates.PortalMapViewer
 * 
 * @param {object} options
 */
export function getViewerConfig(options) {
    options = _.cloneDeep(options);

    // base metadata
    const title = options?.title || "Map Viewer";
    const caption = options?.caption || "";
    const description = options?.description || "";
    const references = options?.references || "";
    const keywords = options?.keywords || [];
    const authors = options?.authors || [];

    // content config
    const map = options?.config?.map || {};
    const baseLayers = options?.config?.baseLayers || {};
    const layers = options?.config?.layers || {};
    const cache = options?.config?.cache || {};
    const structure = options?.config?.structure || [];
    const filters = options?.config?.filters || [];
    const tools = options?.config?.tools || {};
    const indexUrl = options?.config?.indexUrl || null;

    const mapTools = [
        {
            type: "Zoom",
            position: "top-left"
        },
        {
            type: "ProjectionSwitcher",
            position: "top-left"
        },
        {
            type: "FitToScreen",
            position: "top-left"
        },
        {
            type: "DrawTools",
            position: "top-left"
        },
        {
            type: "LayerLegends",
            position: "top-left",
            targetTree: "layer-tree"
        },
        {
            type: "Screenshot",
            position: "top-left"
        },
        {
            type: "BaselayerSwitcher",
            position: "bottom-left",
            infoSidebar: "info-sidebar",
            infoSidebarGroup: "sidebar-info-content"
        },
        {
            type: "ScaleControl",
            position: "bottom-left"
        },
        {
            type: "CoordinateControl",
            position: "bottom-left"
        },
        {
            type: "Attribution",
            position: "bottom-right"
        }
    ].reduce((accumulator, tool) => {
        if (tool.type in tools) {
            if (typeof tools[tool.type] == "object") {
                accumulator.push(Object.assign({}, tool, tools[tool.type]));
            }
        } else {
            accumulator.push(tool);
        }
        return accumulator;
    }, []);

    // Create General Config
    return {
        theme: {},
        elements: [
            // Optional Authors Element
            ... ((authors.length > 0) ? [{
                type: "Authors",
                id: "authors",
                authors: authors
            }] : []),
            // Default Elements
            {
                type: "ProjectInfo",
                id: "info-element",
                title: title,
                description: description,
                keywords: keywords,
                references: references
            },
            {
                type: "Sidebar",
                id: "sidebar",
                position: "left",
                menu: true,
                closeable: true,
                responsive: true,
                groups: [
                    {
                        id: "sidebar-map",
                        title: title,
                        classes: "fas fa-map",
                        active: true
                    },
                    {
                        id: "sidebar-info-group",
                        title: "Information",
                        classes: "fas fa-info",
                        active: false
                    },
                    ... (authors.length > 0) ? [{
                        id: "sidebar-authors-group",
                        title: "Authors & Contacts",
                        classes: "fas fa-users",
                        active: false
                    }] : []
                ]
            },
            {
                type: "Sidebar",
                id: "info-sidebar",
                position: "right",
                underlineHeader: true,
                menu: false,
                closeable: true,
                responsive: true,
                groups: [
                    {
                        id: "sidebar-popup-content"
                    },
                    {
                        id: "sidebar-info-content"
                    }
                ]
            },
            {
                type: "Map",
                id: "map",
                mode: "2D",
                popup: {
                    targetSidebar: "info-sidebar",
                    sidebarGroup: "sidebar-popup-content"
                },
                activeAreaSidebar: "sidebar",
                options: map,
                tools: mapTools
            },
            {
                type: "LayerTree",
                id: "layer-tree",
                title: "Layer Tree",
                targetMap: "map",
                targetSidebar: "info-sidebar",
                infoSidebarGroup: "sidebar-info-content",
                addLayerMethods: [
                    "ows",
                    "dws",
                    "catalog",
                    "importer"
                ],
                indexUrl: indexUrl,
                deleteEnabled: true,
                editEnabled: true,
                addFolderEnabled: true,
                structure: structure
            },
            {
                type: "FileDropArea",
                id: "drag-drop-area",
                dropTarget: "map",
                targetTree: "layer-tree"
            },
            {
                type: "AddFilterButton",
                id: "add-filter-button",
                parentId: "sidebar-map"
            }
        ],
        layout: [
            {
                parent: "#",
                children: [
                    "map",
                    "sidebar",
                    "info-sidebar"
                ]
            },
            {
                parent: "sidebar-map",
                children: ["layer-tree"].concat(filters.map(f => f.id)).concat(["add-filter-button"])
            },
            {
                parent: "sidebar-info-group",
                children: ["info-element"]
            },
            ... (authors.length > 0) ? [{
                parent: "sidebar-authors-group",
                children: ["authors"]
            }] : []
        ],
        layers: layers,
        cache: cache,
        baseLayers: baseLayers,
        filters: filters,
        customFunctions: [],
        templateFunctions: []
    };
}

/**
 * convert a regular viewer config to the
 * PortalViewer config
 * @namespace vef.viewer.templates.DefaultViewer
 */
export function getTemplateConfig(config, originalConfig) {
    config = (config) ? _.cloneDeep(config) : {};
    originalConfig = (originalConfig) ? _.cloneDeep(originalConfig) : {};

    originalConfig.template = "PortalMapViewer";
    originalConfig.config = {
        baseLayers: config?.baseLayers || {},
        layers: config?.layers || {},
        cache: config?.cache || {},
        filters: config.filters || [],
        tools: originalConfig?.config?.tools || {},
        indexUrl: originalConfig?.config?.indexUrl || null,
        map: {},
        structure: []
    };

    // find specific element configs
    config.elements.forEach(element => {
        switch (element.id) {
            case "map":
                originalConfig.config.map = element.options;
                break;
            case "layer-tree":
                originalConfig.config.structure = element.structure;
                break;
        }
    });

    return originalConfig;
}